// **************************************************
// LOAD SERVICE
// **************************************************
//
// Sample Manifest
//
// [
//   {
//     id: 'scene1',
//     urls: [
//       require('src/assets/videos/portraitofher.mp4')
//     ]
//   },
//   {
//     id: 'scene2',
//     urls: [
//       require('src/assets/demo/sequence/000.jpg'),
//       require('src/assets/demo/sequence/001.jpg'),
//       require('src/assets/demo/sequence/002.jpg'),
//     ]
//   }
// ]
//
// **************************************************

import EventEmitter from 'src/helpers/EventEmitter'
import Queue from 'src/helpers/Queue'

const Events = {
  GROUP_PROGRESS: 'groupProgress',
  GROUP_COMPLETED: 'groupCompleted',
  PROGRESS: 'progress',
  COMPLETED: 'completed',
}

class Group {
  constructor(id) {
    Object.assign(this, {
      id: id,
      assets: [],
      progress: 0, // 0 to 1
      completed: false,
    })
  }
}

class Asset {
  constructor(url, groupId) {
    Object.assign(this, {
      url: url,
      groupId: groupId,
      bytesLoaded: 0,
      bytesTotal: 1,
      progress: 0, // 0 to 1
      completed: false,
      callback: null,
    })
  }
}

class AssetService {
  constructor() {
    Object.assign(this, EventEmitter)
    this.concurrency = 2

    // PROGRESS
    this._progress = 0
    this._completed = false

    // GROUPS AND ASSETS
    this.groups = {}
    this.assets = {}
  }

  _onProgress = (data) => {
    let asset = this.assets.filter((asset) => {
      return asset.url === data.url
    })[0]

    asset.bytesTotal = data.total || 1
    asset.bytesLoaded = data.loaded || 0
    asset.progress = asset.bytesLoaded / asset.bytesTotal
    this._updateGroupProgress()
    this._updateTotalProgress()
  }

  _onLoad = (data) => {
    let asset = this.assets.filter((asset) => {
      return asset.url === data.url
    })[0]

    asset.blobUrl = URL.createObjectURL(data.xhr.response)
    asset.bytesTotal = data.total || 1
    asset.bytesLoaded = asset.bytesTotal
    asset.progress = 1
    asset.completed = true

    if (typeof asset.callback === 'function') asset.callback()
  }

  _updateGroupProgress = () => {
    for (const group of this.groups) {
      let progress = 0
      for (const asset of group.assets) {
        progress += asset.progress
      }
      progress /= group.assetsTotal

      if (group.progress !== progress) {
        this.emit(Events.GROUP_PROGRESS, {
          groupId: group.id,
          progress: progress,
        })
        if (progress >= 1) {
          this.emit(Events.GROUP_COMPLETED, { groupId: group.id })
        }
      }
      group.progress = Math.min(progress, 1)
    }
  }

  _updateTotalProgress = () => {
    let progress = 0
    for (const asset of this.assets) {
      progress += asset.progress
    }
    progress /= this.assets.length
    if (this.assets.length === 0) progress = 1

    if (this._progress !== progress) {
      this.emit(Events.PROGRESS, progress)
      if (progress >= 1) {
        this._completed = true
        this.emit(Events.COMPLETED)
      }
    }
    this._progress = Math.min(progress, 1)
  }

  _loadAsset = (asset) => {
    return new Promise((resolve) => {
      let xhr = new XMLHttpRequest()
      xhr.crossOrigin = 'anonymous'
      xhr.responseType = 'blob'

      xhr.onprogress = (evt) => {
        this._onProgress({
          url: asset.url,
          loaded: evt.loaded,
          total: evt.total,
        })
      }

      xhr.onload = () => {
        this._onLoad({
          url: asset.url,
          xhr: xhr,
        })
        resolve()
      }

      xhr.open('GET', asset.url)
      xhr.send()
    })
  }

  _loadAssets = (assets) => {
    return new Promise((resolve) => {
      let q = new Queue(this.concurrency)
      for (let asset of assets) {
        q.add(() => {
          return this._loadAsset(asset)
        })
      }
      if (q.pending === 0) resolve()
      q.on(Queue.Events.EMPTY, () => {
        resolve()
      })
    })
  }

  load = (manifest, concurrency = 2) => {
    this.concurrency = concurrency

    // CREATE ASSETS & GROUPS
    this.assets = []
    this.groups = []

    for (const config of manifest) {
      let group = new Group(config.id)
      this.groups.push(group)

      for (let url of config.urls) {
        // Check if exists
        let asset = this.assets.filter((asset) => {
          return asset.url === url
        })[0]
        if (asset) continue

        // Create asset
        asset = new Asset(url, group.id)
        this.assets.push(asset)
        group.assets.push(asset)
      }

      group.assetsTotal = group.assets.length
    }

    // LOAD
    return new Promise((resolve) => {
      let q = new Queue(1) // Load only 1 group at a time
      for (const config of manifest) {
        let assets = this.assets.filter((asset) => {
          return asset.groupId === config.id
        })
        q.add(() => {
          return this._loadAssets(assets)
        })
      }

      let onComplete = () => {
        this._updateGroupProgress()
        this._updateTotalProgress()
        console.log(`AssetService loaded`)
        resolve()
      }

      if (q.pending === 0) onComplete()
      q.on(Queue.Events.EMPTY, onComplete)
    })
  }

  getAsset = (url) => {
    let asset = this.assets.filter((asset) => {
      return asset.url === url
    })[0]
    return asset
  }
}

let service = new AssetService()
service.Events = Events
window.AssetService = service
export default service
